import { FormField } from '@/components/ui/form-field'
import UnlimitedInput from '@/components/UnlimitedInput'
import AddIcons from '@/icons/AddIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import { useTranslation } from 'react-i18next'
import { Button, GridHookForm, TextField } from 'vnetwork-ui'

// Custom Price Ranges List Component
interface PriceRangesListProps {
    namingControl: string // e.g. "order.products.[0].service_configoptions.[0].prices" or "service_configoptions.0.price_ranges"
    formControl: any
    commitmentValue?: number // To show meaningful ranges from commitment value
}

const PriceRangesList: React.FC<PriceRangesListProps> = ({
    namingControl,
    formControl,
    commitmentValue = 0,
}) => {
    const { t } = useTranslation()

    // Get current price ranges value
    const currentRanges = formControl.watch(namingControl) || []

    const addRange = () => {
        const updatedRanges = [...currentRanges]

        // Smart logic for new range
        let newRange
        if (updatedRanges.length === 0) {
            // First range starts from 0
            newRange = {
                price_start: '0',
                price_end: '999999999',
                price_per_unit: '',
            }
        } else {
            const lastRange = updatedRanges[updatedRanges.length - 1]

            // If last range has unlimited (999999999), create a proper intermediate range
            if (
                lastRange.price_end === '999999999' ||
                Number(lastRange.price_end) >= 999999999
            ) {
                // Suggest a reasonable intermediate value based on price_start
                const lastStart = Number(lastRange.price_start) || 0
                const suggestedEnd = lastStart + Math.max(1000, lastStart * 0.5) // Add 50% or minimum 1000

                // Update previous range to end at suggested value
                updatedRanges[updatedRanges.length - 1] = {
                    ...lastRange,
                    price_end: suggestedEnd.toString(),
                }

                // New range starts where previous ends, keeps same price_per_unit as suggestion
                newRange = {
                    price_start: suggestedEnd.toString(),
                    price_end: '999999999',
                    price_per_unit: lastRange.price_per_unit || '',
                }
            } else {
                // Normal case: start where last range ended
                const nextStart = Number(lastRange.price_end).toString()
                newRange = {
                    price_start: nextStart,
                    price_end: '999999999',
                    price_per_unit: lastRange.price_per_unit || '',
                }
            }
        }

        updatedRanges.push(newRange)
        formControl.setValue(namingControl, updatedRanges, {
            shouldDirty: true,
            shouldTouch: true,
        })
    }

    const removeLastRange = () => {
        if (currentRanges.length === 0) return

        // Only remove the last range
        const updatedRanges = [...currentRanges]
        updatedRanges.pop()

        // If there are still ranges left, set the new last range price_end to 999999999
        if (updatedRanges.length > 0) {
            updatedRanges[updatedRanges.length - 1].price_end = '999999999'
        }

        formControl.setValue(namingControl, updatedRanges, {
            shouldDirty: true,
            shouldTouch: true,
        })
    }

    const updateRange = (
        rangeIndex: number,
        field: string,
        value: string | null
    ) => {
        const updatedRanges = [...currentRanges]
        updatedRanges[rangeIndex] = {
            ...updatedRanges[rangeIndex],
            [field]: value,
        }
        formControl.setValue(namingControl, updatedRanges, {
            shouldDirty: true,
            shouldTouch: true,
        })
    }

    return (
        <div className="">
            {/* Header */}
            <div className="grid grid-cols-4 gap-4 text-xs font-medium text-gray-600 mb-2">
                <span>{t('services.tier')}</span>
                <span>{t('services.usage-from')}</span>
                <span>{t('services.usage-to')}</span>
                <span>{t('services.price-per-unit')}</span>
            </div>

            {/* Empty State */}
            {currentRanges?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg
                            className="w-6 h-6 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                            />
                        </svg>
                    </div>
                    <p className="text-sm font-medium mb-1">
                        {t('services.no-ranges')}
                    </p>
                </div>
            )}

            {/* Price Ranges */}
            {currentRanges?.map((range: any, rangeIndex: number) => {
                if (range?.price_end <= commitmentValue) {
                    return null
                }
                return (
                    <div key={rangeIndex}>
                        <div className="grid grid-cols-4 gap-4 items-end">
                            <GridHookForm
                                actions={[]}
                                formControl={formControl}
                                subForm
                                data={[
                                    {
                                        className: 'col-span-1',
                                    },
                                    {
                                        formItemProps: {
                                            name: `${namingControl}.${rangeIndex}.price_start` as any,
                                            type: 'number',
                                            placeholder: t(
                                                'services.usage-from'
                                            ),
                                            disabled: rangeIndex === 0,
                                        },
                                        className:
                                            rangeIndex ===
                                            currentRanges?.filter(
                                                (r: any) =>
                                                    r?.price_end <=
                                                    commitmentValue
                                            )?.length
                                                ? 'hidden'
                                                : '',
                                    },
                                    {
                                        renderEditComponent: () => (
                                            <TextField
                                                size="small"
                                                disabled
                                                type="number"
                                                value={commitmentValue}
                                            />
                                        ),
                                        className:
                                            rangeIndex !==
                                            currentRanges?.filter(
                                                (r: any) =>
                                                    r?.price_end <=
                                                    commitmentValue
                                            )?.length
                                                ? 'hidden'
                                                : '',
                                    },
                                    {
                                        className:
                                            rangeIndex ===
                                            currentRanges.length - 1
                                                ? 'hidden'
                                                : '',
                                        formItemProps: {
                                            name: `${namingControl}.${rangeIndex}.price_end` as any,
                                            type: 'number',
                                            placeholder: t('services.usage-to'),
                                        },
                                    },
                                    {
                                        className:
                                            rangeIndex !==
                                            currentRanges.length - 1
                                                ? 'hidden'
                                                : '',
                                        renderEditComponent: () => (
                                            <UnlimitedInput
                                                value={range?.price_end}
                                                onChange={(value) =>
                                                    updateRange(
                                                        rangeIndex,
                                                        'price_end',
                                                        value
                                                    )
                                                }
                                                placeholder={t(
                                                    'services.usage-to'
                                                )}
                                                type="number"
                                            />
                                        ),
                                    },
                                    {
                                        renderEditComponent: () => (
                                            <FormField
                                                control={formControl.control}
                                                name={`${namingControl}.${rangeIndex}.price_per_unit`}
                                                type="number"
                                                placeholder={t(
                                                    'services.price-per-unit'
                                                )}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    </div>
                )
            })}
            <div className="gap-4 grid grid-cols-4 justify-end items-end">
                <div className="col-span-2" />
                <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    onClick={removeLastRange}
                    disabled={
                        currentRanges?.filter(
                            (r: any) => r?.price_end > commitmentValue
                        )?.length === 1
                    }
                    startIcon={<DeleteOutlineIcons />}
                    className="w-full border-1 border-dashed border-gray-300 rounded-sm text-gray-500 hover:border-red-300 hover:text-c-error transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {t('services.remove-range')}
                </Button>
                <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    onClick={addRange}
                    startIcon={<AddIcons />}
                    className="w-full border-1 border-dashed rounded-sm border-blue-300 text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                    {t('services.add-range')}
                </Button>
            </div>
        </div>
    )
}

export default PriceRangesList
